import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { FadeIn } from "../components/Reveal";
import { MetaLabel, KineticDivider } from "../components/Bits";
import { NewsletterSignup } from "../components/NewsletterSignup";
import { api, apiError } from "../lib/api";
import { setSeo } from "../lib/seo";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setPost(null); setError(null);
    api.get(`/posts/${slug}`)
      .then((r) => {
        const p = r.data; setPost(p);
        const origin = window.location.origin;
        const img = p.og_image || p.cover_url;
        const absImg = img ? (img.startsWith("http") ? img : `${origin}${img}`) : undefined;
        setSeo({
          title: p.seo_title || `${p.title} · Marella Bolognesi`,
          description: p.meta_description || p.excerpt || undefined,
          canonical: `${origin}/blog/${p.slug}`,
          og: { title: p.seo_title || p.title, description: p.meta_description || p.excerpt, type: "article", url: `${origin}/blog/${p.slug}`, image: absImg },
        });
      })
      .catch((e) => setError(apiError(e)));
    window.scrollTo(0, 0);
  }, [slug]);

  if (error) return (
    <div className="min-h-screen pt-32 text-center px-6">
      <p className="font-serif text-3xl text-ink">{error}</p>
      <Link to="/blog" className="mt-6 inline-block text-[11px] uppercase tracking-[0.2em] link-underline">← Back to the Journal</Link>
    </div>
  );
  if (!post) return <div className="min-h-screen pt-32 text-center text-sm text-ink-soft animate-pulse">Loading article…</div>;

  return (
    <div data-testid="blog-post-page" className="pt-20">
      {/* Header */}
      <section className="mx-auto max-w-[900px] px-6 lg:px-12 pt-16 lg:pt-24 pb-10">
        <FadeIn><Link to="/blog" className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] text-ink-soft link-underline mb-8"><ArrowLeft size={14} /> The Journal</Link></FadeIn>
        <FadeIn delay={0.05}><MetaLabel>{post.date}{post.category ? ` · ${post.category}` : ""}</MetaLabel></FadeIn>
        <FadeIn delay={0.1}>
          <h1 className="mt-6 font-serif font-light text-ink text-4xl sm:text-5xl lg:text-[3.6rem] leading-[1.03] tracking-tight" data-testid="post-title">{post.title}</h1>
        </FadeIn>
        {post.subtitle && (
          <FadeIn delay={0.15}>
            <p className="mt-6 font-serif italic text-xl lg:text-2xl text-ink-soft leading-snug max-w-2xl" data-testid="post-subtitle">{post.subtitle}</p>
          </FadeIn>
        )}
      </section>

      {/* Cover */}
      {post.cover_url && (
        <section className="mx-auto max-w-[1200px] px-6 lg:px-12 mb-14">
          <FadeIn delay={0.15}>
            <div className="overflow-hidden">
              <img src={post.cover_url} alt={post.title}
                className={`w-full object-cover ${post.cover_portrait_mobile ? "aspect-[4/5] sm:aspect-[16/9]" : "aspect-[16/9]"}`}
                style={post.cover_position ? { objectPosition: post.cover_position } : undefined} />
            </div>
          </FadeIn>
        </section>
      )}

      {/* Body */}
      <article data-testid="blog-post-body"
        className="mx-auto max-w-[720px] px-6 lg:px-0 pb-20 text-lg font-light text-ink-soft leading-[1.85]
          [&_p]:mb-6
          [&_h2]:font-serif [&_h2]:text-ink [&_h2]:text-3xl [&_h2]:lg:text-4xl [&_h2]:font-light [&_h2]:mt-14 [&_h2]:mb-5 [&_h2]:tracking-tight
          [&_h3]:font-serif [&_h3]:text-ink [&_h3]:text-2xl [&_h3]:lg:text-3xl [&_h3]:font-normal [&_h3]:mt-12 [&_h3]:mb-4
          [&_h4]:font-sans [&_h4]:uppercase [&_h4]:tracking-[0.2em] [&_h4]:text-[12px] [&_h4]:text-accent [&_h4]:mt-10 [&_h4]:mb-3
          [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-6 [&_ul]:space-y-2
          [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-6 [&_ol]:space-y-2
          [&_li]:pl-1
          [&_blockquote]:border-l-2 [&_blockquote]:border-accent [&_blockquote]:pl-6 [&_blockquote]:italic [&_blockquote]:font-serif [&_blockquote]:text-ink [&_blockquote]:text-2xl [&_blockquote]:my-10
          [&_strong]:text-ink [&_strong]:font-medium
          [&_a]:text-ink [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-accent"
        dangerouslySetInnerHTML={{ __html: post.content_html || "<p>This article is coming soon.</p>" }}
      />

      {/* Optional editorial bridge to a course — restrained, clearly separated from the body */}
      {post.bridge && (
        <section className="mx-auto max-w-[720px] px-6 lg:px-0 pb-20" data-testid="post-bridge">
          <div className="border-t border-line pt-12">
            <MetaLabel>{post.bridge.heading}</MetaLabel>
            <div className="mt-6 text-base lg:text-lg font-light text-ink-soft leading-relaxed [&_p]:mb-4 [&_em]:text-ink [&_em]:not-italic [&_em]:font-normal"
              dangerouslySetInnerHTML={{ __html: post.bridge.html }} />
            <Link to={post.bridge.cta_href} data-testid="post-bridge-cta"
              className="group mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-ink border-b border-ink pb-1 hover:text-accent hover:border-accent transition-colors">
              {post.bridge.cta_label} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </section>
      )}

      {/* Notes from Il Salotto — after the article */}
      <section className="border-y border-line bg-ink text-bg">
        <div className="mx-auto max-w-[900px] px-6 lg:px-12 py-16 lg:py-20">
          <NewsletterSignup source={`article:${slug}`} tone="dark" />
        </div>
      </section>

      {/* Signature footer */}
      <KineticDivider />
      <section className="mx-auto max-w-[900px] px-6 lg:px-12 py-20 text-center">
        <p className="text-[11px] uppercase tracking-ultra text-accent">Written by</p>
        <p className="mt-4 font-serif text-3xl text-ink">Marella Bolognesi</p>
        <p className="mt-2 text-sm text-ink-soft">Estate Management &amp; Education · Miami, Florida</p>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <Link to="/blog" className="border border-ink text-ink px-8 py-4 text-[11px] uppercase tracking-[0.2em] hover:bg-ink hover:text-bg transition-colors">More from the Journal</Link>
          <Link to="/salotto-digitale" className="group bg-ink text-bg px-8 py-4 text-[11px] uppercase tracking-[0.2em] hover:bg-[#33312E] transition-colors flex items-center gap-2">
            Explore Il Salotto Digitale <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </div>
  );
}
